import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { ServiceOrderFormOptions, ServiceOrderListResult } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";

/** Serviços › Ordens de serviço (RF-SER-03): por número, cliente, equipamento, nº de série ou técnico, e situação. */
export function ServiceOrdersListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusId, setStatusId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const term = q.trim();
  const canCreate = can(perms, "service_orders", "create");
  // Situações vêm das opções do formulário; quem só lê fica sem o filtro de situação.
  const options = useQuery({
    queryKey: ["service-orders", companyId, "form-options"],
    enabled: canCreate || can(perms, "service_orders", "update"),
    queryFn: () => api<ServiceOrderFormOptions>("/service-orders/form-options"),
  });
  const list = useInfiniteQuery({
    queryKey: ["service-orders", companyId, "list", term, statusId, from, to],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ServiceOrderListResult>(
        `/service-orders?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(statusId ? { statusId } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const create = () => navigate({ to: "/servicos/ordens-de-servico/nova" });

  return (
    <>
      <ContentHeader
        title="Ordens de serviço"
        breadcrumb={["Serviços", "Ordens de serviço"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={create}>
              Incluir ordem de serviço
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="os-q">
              Pesquisar ordem de serviço
            </label>
            <input
              id="os-q"
              type="search"
              className="form-control"
              placeholder="Número, cliente, equipamento, série ou técnico"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {options.data && (
            <div className="col-md-3">
              <label className="visually-hidden" htmlFor="os-status">
                Situação
              </label>
              <select
                id="os-status"
                className="form-select"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">Todas as situações</option>
                {options.data.statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="os-from">
              Entrada a partir de
            </label>
            <input
              id="os-from"
              type="date"
              className="form-control"
              title="Entrada a partir de"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="os-to">
              Entrada até
            </label>
            <input
              id="os-to"
              type="date"
              className="form-control"
              title="Entrada até"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma ordem de serviço"
            description="Registre a entrada do equipamento, os serviços e as peças; ao finalizar, as peças saem do estoque, as contas a receber são lançadas e as notas (NFS-e e NF-e) podem ser emitidas."
            actions={canCreate ? [{ label: "Incluir ordem de serviço", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Ordens de serviço">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Entrada</th>
                    <th scope="col">Cliente</th>
                    <th scope="col">Equipamento</th>
                    <th scope="col">Técnico</th>
                    <th scope="col" className="text-end">
                      Total
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/servicos/ordens-de-servico/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                      </td>
                      <td>{formatDateBR(r.entryDate)}</td>
                      <td>{r.contactName}</td>
                      <td>{r.equipment ?? "—"}</td>
                      <td>{r.technician ?? "—"}</td>
                      <td className="text-end">{formatBRL(r.totalCents)}</td>
                      <td>
                        <StatusBadge tone={r.status.color}>{r.status.label}</StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-3 p-3">
              {summary && summary.count >= 0 && (
                <span className="small text-muted" data-testid="os-summary">
                  {summary.count} {summary.count === 1 ? "ordem" : "ordens"}
                  {summary.totalCents !== null && ` · ${formatBRL(summary.totalCents)}`}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
